import { useEffect, useRef, useState } from 'react';
import { Button, Row, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import AnalyzingProgressModal from 'src/components/AnalyzingProgressModal/AnalyzingProgressModal';
import PatientInfoWithControls from 'src/components/custom_components/PatientInfoWithControls';

import { Api } from 'src/models/Api.model';
import { Eye, EyePropertyCode } from 'src/models/Eye.model';
import { CancelStatus } from 'src/models/GalenData/CancelStatus.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import { QualityPrecheckEnum } from 'src/models/Grading.model';
import { defaultBackendStatus } from 'src/models/InitialBackendStatus';
import { I18NextNS } from 'src/models/Language.model';
import { EyesPosition } from 'src/models/Session.model';

import useCancelCaptureImages from 'src/hooks/camera/useCancelCaptureImages';
import useCaptureImage from 'src/hooks/camera/useCaptureImage';
import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useSubmitSession from 'src/hooks/useSubmitSession';

import CameraPreviewColumn from './CameraPreviewColumn';
import ImagePreviewColumn from './ImagePreviewColumn';
import SmallPupilModal from './SmallPupilModal';

const SessionPreview = () => {
  const {
    leftUrl,
    rightUrl,
    setLeftUrl,
    setRightUrl,
    setBackendStatus,
    setTotalLeftImageAttempt,
    setTotalRightImageAttempt,
    setIsAnalyzing,
    session,
    resetSession,
    qualityData,
    setQualityData,
    updateQualityData,
    isCapturing,
    isCanceling,
    isUploadingLeftImage,
    isUploadingRightImage,
  } = useRemoteBoundStore();

  const hasQualityPrecheck = false;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const { userId, practiceId } = useGetCurrentUser();
  const { getErrorMessage } = useGetGalenErrorMsg();
  const { handleHttpRequestError } = useHandleHttpRequestError();

  const location = useLocation<{
    isResume: boolean;
    leftImageFileName: string;
    rightImageFileName: string;
  }>();

  const isResume = location.state?.isResume || false;

  const [isShowAnalyzingModal, setIsShowAnalyzingModal] = useState(false);
  const [isQualityCheckDone, setIsQualityCheckDone] = useState(false);
  const [cancelStatus, setCancelStatus] = useState<CancelStatus>(
    CancelStatus.NotCanceled,
  );

  const {
    submitImage,
    pollQualityStatus,
    setUploadStatus,
    createRetakeSession,
    submitRetakeImage,
  } = useSubmitSession();

  const handleCloseAnalyzingModal = () => {
    setIsShowAnalyzingModal(false);
  };

  const handleSubmitSession = async () => {
    try {
      setIsShowAnalyzingModal(true);

      if (!practiceId) {
        throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_IS_REQUIRED));
      }

      setBackendStatus(defaultBackendStatus);

      if (!userId) {
        throw new Error(
          t('message.error.userIdNotFound', {
            ns: I18NextNS.COMMON,
          }),
        );
      }

      await setUploadStatus();

      setIsAnalyzing(true);
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      Api.alertBox({
        title: t('bootbox.title.Error', {
          ns: I18NextNS.COMMON,
        }),
        message: errorMessage,
        locale: language,
        type: 'error',
      });
    }
  };

  const { handleCancelCapture } = useCancelCaptureImages();

  const handleCancel = () => {
    Api.confirmBox({
      title: t('bootbox.title.Confirm', { ns: I18NextNS.COMMON }),
      message: t('newExam.confirmExitImaging'),
      callback: (result) => {
        if (!result) {
          return;
        }
        setCancelStatus(CancelStatus.BothEyesCanceled);
        handleCancelCapture();
      },
      locale: language,
    });
  };

  useEffect(() => {
    if (isResume) {
      if (session.leftImageFile) {
        setLeftUrl(session.leftImageFile);
      }

      if (session.rightImageFile) {
        setRightUrl(session.rightImageFile);
      }

      if (session.graded) {
        setIsShowAnalyzingModal(true);
      }
    }
    // We only want to run this effect when the component mounts, not when the component updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { captureLeftImage, captureRightImage, captureBothImages } =
    useCaptureImage();

  const handleRetakeCapture = (position: EyesPosition) => {
    if (position === EyesPosition.BOTH) {
      captureBothImages();
      setCancelStatus(CancelStatus.NotCanceled);
    }
    if (position === EyesPosition.LEFT) {
      setCancelStatus(CancelStatus.RightEyeCanceled);
      captureLeftImage();
    }
    if (position === EyesPosition.RIGHT) {
      setCancelStatus(CancelStatus.LeftEyeCanceled);
      captureRightImage();
    }
  };

  const handleRetake = async (position: EyesPosition) => {
    setIsQualityCheckDone(true); // stop polling before remote is set correctly
    if (position === EyesPosition.BOTH) {
      setLeftUrl('');
      setRightUrl('');

      // Have to put this after set_Url to avoid cypress clicking the submit button too quickly
      await createRetakeSession();
      handleRetakeCapture(position);
      return;
    }

    if (position === EyesPosition.LEFT) {
      setLeftUrl('');

      // Have to put this after set_Url to avoid cypress clicking the submit button too quickly
      const sessionId = await createRetakeSession();

      const basedata = {
        sessionId,
        side: EyePropertyCode.RIGHT,
        imageUrl: rightUrl,
        imageFileName: isResume
          ? location.state?.rightImageFileName
          : rightUrl.split('/').pop() || '',
      };

      const data = hasQualityPrecheck
        ? {
            ...basedata,
            qualityData: {
              RightEyeQuality: qualityData.RightEyeQuality,
              RightEyeQualityJson: qualityData.RightEyeQualityJson,
              LeftEyeQuality: QualityPrecheckEnum.Retake,
            },
          }
        : basedata;
      if (hasQualityPrecheck) {
        updateQualityData({ LeftEyeQuality: QualityPrecheckEnum.Retake });
        setIsQualityCheckDone(false);
      }
      submitRetakeImage(data);
      handleRetakeCapture(position);
      return;
    }

    if (position === EyesPosition.RIGHT) {
      setRightUrl('');

      // Have to put this after set_Url to avoid cypress clicking the submit button too quickly
      const sessionId = await createRetakeSession();

      const baseData = {
        sessionId,
        side: EyePropertyCode.LEFT,
        imageUrl: leftUrl,
        imageFileName: isResume
          ? location.state?.leftImageFileName
          : leftUrl.split('/').pop() || '',
      };

      const data = hasQualityPrecheck
        ? {
            ...baseData,
            qualityData: {
              LeftEyeQuality: qualityData.LeftEyeQuality,
              LeftEyeQualityJson: qualityData.LeftEyeQualityJson,
              RightEyeQuality: QualityPrecheckEnum.Retake,
            },
          }
        : baseData;

      if (hasQualityPrecheck) {
        updateQualityData({ RightEyeQuality: QualityPrecheckEnum.Retake });
        setIsQualityCheckDone(false);
      }
      submitRetakeImage(data);
      handleRetakeCapture(position);
    }
  };

  const resubmitExistingSession = async () => {
    const deviceDataId = await createRetakeSession();
    await Promise.all([
      submitImage({
        side: EyePropertyCode.RIGHT,
        imageUrl: rightUrl,
        imageFileName: rightUrl.split('/').pop() || '',
        hasQualityPrecheck,
        deviceDataId,
      }),
      submitImage({
        side: EyePropertyCode.LEFT,
        imageUrl: leftUrl,
        imageFileName: leftUrl.split('/').pop() || '',
        hasQualityPrecheck,
        deviceDataId,
      }),
    ]);
    setUploadStatus(deviceDataId);
  };

  useEffect(() => {
    if (
      hasQualityPrecheck &&
      (leftUrl || rightUrl) &&
      !intervalRef.current &&
      !isQualityCheckDone
    ) {
      const pollWithStopCondition = async () => {
        const qualityResult = await pollQualityStatus();

        const hasLeftQuality =
          qualityResult?.LeftEyeQuality !== undefined &&
          qualityResult?.LeftEyeQuality !== QualityPrecheckEnum.Retake;
        const hasRightQuality =
          qualityResult?.RightEyeQuality !== undefined &&
          qualityResult?.RightEyeQuality !== QualityPrecheckEnum.Retake;

        if (hasLeftQuality && hasRightQuality) {
          setIsQualityCheckDone(true);
          // Both quality data are available, stop polling
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      };
      intervalRef.current = setInterval(pollWithStopCondition, 1000);
    } else if (!hasQualityPrecheck && leftUrl && rightUrl) {
      // If quality precheck is disabled and we have both images, mark as done immediately
      setIsQualityCheckDone(true);
    }
  }, [
    leftUrl,
    pollQualityStatus,
    rightUrl,
    isQualityCheckDone,
    hasQualityPrecheck,
  ]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Reset image URLs and quality data to defaults on unmount
      setLeftUrl('');
      setRightUrl('');
      setTotalLeftImageAttempt(0);
      setTotalRightImageAttempt(0);
      if (hasQualityPrecheck) {
        setQualityData({
          LeftEyeQuality: undefined,
          LeftEyeQualityJson: undefined,
          RightEyeQuality: undefined,
          RightEyeQualityJson: undefined,
        });
      }
    };
  }, [
    setLeftUrl,
    setRightUrl,
    setQualityData,
    hasQualityPrecheck,
    setTotalLeftImageAttempt,
    setTotalRightImageAttempt,
  ]);

  return (
    <div className="d-flex flex-column h-100 overflow-hidden">
      <PatientInfoWithControls />
      <div
        className="d-flex gap-3 flex-fill overflow-auto"
        style={{ minHeight: 0 }}
      >
        <CameraPreviewColumn isExpanded={!!(leftUrl && rightUrl)} />
        <ImagePreviewColumn
          imageUrl={leftUrl}
          eyeType={Eye.LEFT}
          onRetake={() => {
            handleRetakeCapture(EyesPosition.LEFT);
          }}
          retakeLabel={t('newExam.retakeLeftEye')}
          isExpanded={!!(leftUrl && rightUrl)}
          isRetakeDisabled={isCapturing}
          isCanceling={isCanceling}
          hasCanceled={
            cancelStatus === CancelStatus.LeftEyeCanceled ||
            cancelStatus === CancelStatus.BothEyesCanceled
          }
          quality={hasQualityPrecheck ? qualityData.LeftEyeQuality : undefined}
          hasQualityPrecheck={hasQualityPrecheck}
        />
        <ImagePreviewColumn
          imageUrl={rightUrl}
          eyeType={Eye.RIGHT}
          onRetake={() => {
            handleRetakeCapture(EyesPosition.RIGHT);
          }}
          retakeLabel={t('newExam.retakeRightEye')}
          isExpanded={!!(leftUrl && rightUrl)}
          isRetakeDisabled={isCapturing}
          isCanceling={isCanceling}
          hasCanceled={
            cancelStatus === CancelStatus.RightEyeCanceled ||
            cancelStatus === CancelStatus.BothEyesCanceled
          }
          quality={hasQualityPrecheck ? qualityData.RightEyeQuality : undefined}
          hasQualityPrecheck={hasQualityPrecheck}
        />
      </div>
      <Row style={{ marginTop: 'auto' }}>
        <Stack direction="horizontal" gap={3} className="d-flex pt-2">
          <div className="d-flex gap-3">
            <Button
              variant="danger"
              disabled={!isCapturing || isCanceling}
              onClick={() => {
                handleCancel();
              }}
            >
              {isCanceling
                ? t('newExam.cancelingImaging')
                : t('newExam.cancelImaging')}
            </Button>
          </div>
          <div className="ms-auto d-flex gap-3">
            <Button
              variant="primary-100"
              onClick={() => {
                handleRetakeCapture(EyesPosition.BOTH);
              }}
              disabled={isCapturing || isCanceling}
            >
              {t('newExam.retake')}
            </Button>
            <Button
              variant="primary"
              className="w-200px shadow"
              onClick={() => {
                handleSubmitSession();
              }}
              disabled={
                !leftUrl ||
                !rightUrl ||
                isUploadingLeftImage ||
                isUploadingRightImage ||
                (hasQualityPrecheck && !isQualityCheckDone)
              }
            >
              {t('newExam.submit')}
            </Button>
          </div>
        </Stack>
      </Row>

      {isShowAnalyzingModal && (
        <AnalyzingProgressModal
          handleRetake={handleRetake}
          session={session}
          handleCloseAnalyzingProgressModal={handleCloseAnalyzingModal}
          handleResetSession={resetSession}
          resubmitExistingSession={resubmitExistingSession}
        />
      )}
      <SmallPupilModal />
    </div>
  );
};

export default SessionPreview;
